import React from "react";

/**
 * Reusable pagination: "Showing X–Y of Z" + Previous / 1 2 3 ... / Next
 * @param {number} currentPage - 1-based
 * @param {number} totalPages
 * @param {number} totalItems
 * @param {number} pageSize
 * @param {(page: number) => void} onPageChange
 * @param {string} [itemName] - e.g. "orders" for "Showing 1–10 of 45 orders"
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  itemName = "items",
}) => {
  if (totalItems === 0) return null;
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalItems);

  const prevDisabled = currentPage <= 1;
  const nextDisabled = currentPage >= totalPages;

  // Page numbers to show: first, last, current and neighbours, with ellipsis
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    [currentPage - 2, currentPage + 2].forEach((p) => {
      if (p >= 1 && p <= totalPages) pages.add(p);
    });
    return Array.from(pages).sort((a, b) => a - b);
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 dark:border-white/10 pt-4">
      <p className="text-sm text-navy-600 dark:text-navy-300">
        Showing <span className="font-semibold">{from}</span>–
        <span className="font-semibold">{to}</span> of{" "}
        <span className="font-semibold">{totalItems}</span> {itemName}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={prevDisabled}
          className="min-w-[2rem] rounded-lg border border-gray-300 dark:border-white/20 px-2 py-1.5 text-sm font-medium text-navy-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
        >
          Previous
        </button>
        {pageNumbers.map((num, i) => {
          const showEllipsisBefore = i > 0 && num - pageNumbers[i - 1] > 1;
          return (
            <React.Fragment key={num}>
              {showEllipsisBefore && (
                <span className="px-1 text-gray-400 dark:text-navy-400">…</span>
              )}
              <button
                type="button"
                onClick={() => onPageChange(num)}
                className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm font-medium transition ${
                  num === currentPage
                    ? "bg-brand-500 text-white border border-brand-500"
                    : "border border-gray-300 dark:border-white/20 text-navy-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10"
                }`}
              >
                {num}
              </button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={nextDisabled}
          className="min-w-[2rem] rounded-lg border border-gray-300 dark:border-white/20 px-2 py-1.5 text-sm font-medium text-navy-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
